import React from 'react';
import {
  AgnesPopover,
  AgnesPopoverTrigger,
  AgnesPopoverContent,
  AgnesButton,
  AgnesDateRangePickerInput,
  AgnesSwitch,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
} from '@agnes/ui';
import { Filter, Trash2, X } from 'lucide-react';
import type { CessaoFiltros } from '@/lib/cessao';
import type { OpcoesFiltro } from '@/stores/useCessaoStore';
import { labelFiltro } from '@/lib/filtros';

// estado interno do popover: um valor por campo (single-select). Vira lista no submit.
type FiltroForm = {
  origem: string;
  destino: string;
  regime: string;
  periodo: string[];
  declaracao: string; // 'com' | 'sem' | ''
};

const VAZIO: FiltroForm = { origem: '', destino: '', regime: '', periodo: [], declaracao: '' };

const TRIGGER_CLS =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

const DECLARACAO_OPTIONS = [
  { value: 'com', label: 'Com declaração (ônus do cedente)' },
  { value: 'sem', label: 'Sem declaração' },
];

const MS_DIA = 86_400_000;

// string[] de datas ISO -> {startDate, endDate}. Vazio/parcial/invertido/>6meses -> {}.
export function parsePeriodo(periodo: string[]): { startDate?: string; endDate?: string } {
  if (!periodo || periodo.length === 0) return {};
  const start = periodo[0];
  const end = periodo[periodo.length - 1];
  if (!start || !end) return {};
  // limita a 6 meses (~186 dias), paridade com o Vue.
  const diff = (new Date(end).getTime() - new Date(start).getTime()) / MS_DIA;
  if (diff < 0 || diff > 186) return {};
  return { startDate: start, endDate: end };
}

// declaração: 'com' -> true (ônus cedente), 'sem' -> false, '' -> null (ambos).
function toDeclaracao(v: string): boolean | null {
  if (v === 'com') return true;
  if (v === 'sem') return false;
  return null;
}

function toFiltros(f: FiltroForm, isCeded: boolean | null): CessaoFiltros {
  const one = (v: string) => (v ? [v] : []);
  return {
    originCnpjList: one(f.origem),
    destinationCnpjList: one(f.destino),
    tpRegPrevList: one(f.regime),
    originRemunDeclarationToggle: toDeclaracao(f.declaracao),
    isCeded,
    ...parsePeriodo(f.periodo),
  };
}

type Props = {
  opcoes: OpcoesFiltro;
  onApply: (f: CessaoFiltros) => void;
  count?: number;
};

type SelectFieldProps = {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
};
function SelectField({ value, onValueChange, placeholder, options }: SelectFieldProps) {
  return (
    <AgnesSelect value={value} onValueChange={onValueChange}>
      <AgnesSelectTrigger className={TRIGGER_CLS}>
        <AgnesSelectValue placeholder={placeholder} />
      </AgnesSelectTrigger>
      <AgnesSelectContent>
        {options.map((o) => (
          <AgnesSelectItem key={o.value} value={o.value}>
            {o.label}
          </AgnesSelectItem>
        ))}
      </AgnesSelectContent>
    </AgnesSelect>
  );
}

export default function CessaoFilter({ opcoes, onApply, count = 0 }: Props) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState<FiltroForm>(VAZIO);
  // Cedido / Não cedido: mutuamente exclusivos -> isCeded true/false/null.
  const [cedido, setCedido] = React.useState(false);
  const [naoCedido, setNaoCedido] = React.useState(false);
  const set =
    (k: Exclude<keyof FiltroForm, 'periodo'>) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const isCeded: boolean | null = cedido ? true : naoCedido ? false : null;

  const onCedido = (v: boolean) => {
    setCedido(v);
    if (v) setNaoCedido(false);
  };
  const onNaoCedido = (v: boolean) => {
    setNaoCedido(v);
    if (v) setCedido(false);
  };

  const aplicar = () => {
    onApply(toFiltros(f, isCeded));
    setOpen(false);
  };
  const limpar = () => {
    setF(VAZIO);
    setCedido(false);
    setNaoCedido(false);
    onApply(toFiltros(VAZIO, null));
  };

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          aria-label={count > 0 ? `Filtro, ${count} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', count)}
        </AgnesButton>
      </AgnesPopoverTrigger>

      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto px-4 py-4">
          <SelectField
            value={f.origem}
            onValueChange={set('origem')}
            placeholder="Origem"
            options={opcoes.origem}
          />
          <SelectField
            value={f.destino}
            onValueChange={set('destino')}
            placeholder="Destino"
            options={opcoes.destino}
          />
          <SelectField
            value={f.regime}
            onValueChange={set('regime')}
            placeholder="Regime previdenciário"
            options={opcoes.regime}
          />
          <AgnesDateRangePickerInput
            value={f.periodo}
            onChange={(dates) => setF((s) => ({ ...s, periodo: dates }))}
            label="Período"
            dataQa="cessao-filtro-periodo"
          />

          <div>
            <p className="mb-2 text-sm font-medium text-agnes-text">Situação de cessão</p>
            <div className="flex flex-col gap-3">
              <AgnesSwitch label="Cedido" checked={cedido} onCheckedChange={onCedido} />
              <AgnesSwitch label="Não cedido" checked={naoCedido} onCheckedChange={onNaoCedido} />
            </div>
          </div>

          <SelectField
            value={f.declaracao}
            onValueChange={set('declaracao')}
            placeholder="Declaração de remuneração no órgão de origem"
            options={DECLARACAO_OPTIONS}
          />
        </div>

        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton variant="primary" onClick={aplicar} className="w-full">
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}
